import { useState, useEffect, useMemo } from 'react'
import { Ticket, AdminType, User, Building, Center } from '../types'
import { useAuth } from '../hooks/useAuth'
import { useDeepLinkById } from '../hooks/useDeepLinkById'
import { useAlert } from '../contexts/AlertContext'
import { loadTickets, updateTicketStatus, loadUsers, loadBuildings, addUser, deleteUser, addBuilding, deleteBuilding, loadCenters, addCenter, deleteCenter, accelerateTicket, deleteTicket, addTicketComment } from '../lib/api'
import { updateUser } from '../lib/apiPhpAdapter'
import { getSetting, setSetting, loadPurchaseRequests, deletePurchaseRequest, importUsersFromCSV, loadPropertyMoveTickets, deletePropertyMoveTicket } from '../lib/apiPhpAdapter'
import TicketList from '../components/TicketList'
import TicketsTable from '../components/TicketsTable'
import AssignExecutorModal from '../components/AssignExecutorModal'
import Pagination from '../components/Pagination'
import UserManagement from '../components/UserManagement'
import BuildingManagement from '../components/BuildingManagement'
import CenterManagement from '../components/CenterManagement'
import ExecutorRoleManagement from '../components/ExecutorRoleManagement'
import ExecutorStatistics from '../components/ExecutorStatistics'
import ITAdminInfoEditor from '../components/ITAdminInfoEditor'
import { ItAssetsAccessPanel } from '../components/ItAssetsAccessPanel'
import WeeklyNewsEditor from '../components/WeeklyNewsEditor'
import SchoolEmployeesEditor from '../components/SchoolEmployeesEditor'
import DatabaseBackup from '../components/DatabaseBackup'
import { ModuleDevAccessPanel } from '../components/ModuleDevAccessPanel'
import NotesPanel from '../components/NotesPanel'
import PurchaseRequestCard from '../components/PurchaseRequestCard'
import SuppliersManagement from '../components/SuppliersManagement'
import { highlightTextMultiple } from '../utils/textHighlight'
import { useIsMobile } from '../hooks/useMediaQuery'
import './AdminView.css'
import '../styles/common-buttons.css'

interface AdminViewProps {
  adminType: AdminType
}

function AdminView({ adminType }: AdminViewProps) {
  const { user } = useAuth()
  const { showAlert } = useAlert()
  const [allTickets, setAllTickets] = useState<Ticket[]>([])
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null)
  const [loading, setLoading] = useState(true)
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards')
  const [statusFilter, setStatusFilter] = useState<string | null>(null)
  const [ticketTypeFilter, setTicketTypeFilter] = useState<'all' | 'my' | 'building_admin'>('all') // Фильтр для разделения своих заданий и заданий spravce_budov
  const [ticketsSearchText, setTicketsSearchText] = useState<string>('')
  const [activeTab, setActiveTab] = useState<'tickets' | 'settings' | 'notes'>('tickets')
  const [highlightId, setHighlightId] = useState<string | null>(null)

  // Пагинация
  const [currentPage, setCurrentPage] = useState(1)
  const isMobile = useIsMobile()
  const itemsPerPage = isMobile ? 20 : 100
  const [settingsSubTab, setSettingsSubTab] = useState<'users' | 'buildings' | 'centers' | 'executors' | 'system' | 'purchase_requests' | 'property_move_requests' | 'suppliers' | 'it_admin_info' | 'weekly_news' | 'school_employees' | 'module_access' | 'backup' | 'notes'>('system')
  const [showStatistics, setShowStatistics] = useState(false)

  // Управление заявками на покупки
  const [purchaseRequests, setPurchaseRequests] = useState<any[]>([])
  const [purchaseRequestsLoading, setPurchaseRequestsLoading] = useState(false)
  const [purchaseRequestsSearchText, setPurchaseRequestsSearchText] = useState('')
  const [purchaseRequestsStatusFilter, setPurchaseRequestsStatusFilter] = useState<string>('all')
  const [purchaseRequestsViewMode, setPurchaseRequestsViewMode] = useState<'cards' | 'table'>('table')
  const [purchaseRequestsCurrentPage, setPurchaseRequestsCurrentPage] = useState(1)
  const purchaseRequestsItemsPerPage = isMobile ? 20 : 100

  // Управление заявками на перемещение маетка
  const [propertyMoveRequestsSearchText, setPropertyMoveRequestsSearchText] = useState('')
  const [propertyMoveRequestsStatusFilter, setPropertyMoveRequestsStatusFilter] = useState<string>('all')
  const [propertyMoveRequestsCurrentPage, setPropertyMoveRequestsCurrentPage] = useState(1)
  const propertyMoveRequestsItemsPerPage = isMobile ? 20 : 100

  // Управление заявками на перемещение маетка
  const [propertyMoveRequests, setPropertyMoveRequests] = useState<any[]>([])
  const [propertyMoveRequestsLoading, setPropertyMoveRequestsLoading] = useState(false)

  // КРИТИЧЕСКАЯ ПРОВЕРКА: Убеждаемся, что adminType соответствует реальному admin_type пользователя
  useEffect(() => {
    if (user && user.role === 'admin') {
      const userAdminType = user.admin_type

      // Если пользователь пытается использовать не свой тип администратора - блокируем
      if (userAdminType === 'spravce_budov' && adminType === 'spravce_it') {
        console.error('SECURITY: spravce_budov пытается получить доступ как spravce_it')
        alert('Nemáte oprávnění k přístupu jako správce IT. Přístup zamítnut.')
        // Перенаправляем на выбор роли
        window.location.href = '/app/select-role'
        return
      }

      // Если adminType не соответствует реальному типу пользователя - исправляем
      if (userAdminType && adminType !== userAdminType) {
        console.warn(`AdminType mismatch: user has ${userAdminType}, but component received ${adminType}. Fixing...`)
        // В этом случае нужно перенаправить пользователя на правильный тип
        // Но так как мы не можем изменить пропс, просто блокируем доступ
        if (userAdminType === 'spravce_budov') {
          alert('Přístup zamítnut. Jste správce budov, ne správce IT.')
          window.location.href = '/app/select-role'
          return
        }
      }
    }
  }, [user, adminType])

  // Устанавливаем правильный подраздел настроек в зависимости от типа администратора
  useEffect(() => {
    // Дополнительная проверка безопасности
    if (user && user.role === 'admin' && user.admin_type === 'spravce_budov' && adminType === 'spravce_it') {
      console.error('SECURITY VIOLATION: spravce_budov пытается получить доступ как spravce_it')
      return
    }

    if (adminType === 'spravce_budov') {
      // Если spravce_budov пытается открыть раздел, доступный только для IT, перенаправляем на buildings
      if (settingsSubTab === 'system' || settingsSubTab === 'users' || settingsSubTab === 'centers' || settingsSubTab === 'property_move_requests' || settingsSubTab === 'it_admin_info' || settingsSubTab === 'school_employees' || settingsSubTab === 'module_access' || settingsSubTab === 'backup') {
        setSettingsSubTab('buildings')
      }
    } else if (adminType === 'spravce_it' && activeTab === 'settings') {
      // Если IT администратор открывает настройки, устанавливаем первый доступный подраздел
      // НО не меняем, если уже выбран it_admin_info или weekly_news
      if (settingsSubTab !== 'users' && settingsSubTab !== 'buildings' && settingsSubTab !== 'centers' && settingsSubTab !== 'system' && settingsSubTab !== 'purchase_requests' && settingsSubTab !== 'property_move_requests' && settingsSubTab !== 'suppliers' && settingsSubTab !== 'it_admin_info' && settingsSubTab !== 'weekly_news' && settingsSubTab !== 'school_employees' && settingsSubTab !== 'module_access' && settingsSubTab !== 'backup') {
        setSettingsSubTab('system')
      }
    }
  }, [adminType, settingsSubTab, activeTab, user])

  // Отладка: проверяем условия отображения кнопки
  useEffect(() => {
    if (adminType === 'spravce_it') {
      console.log('AdminView: IT Admin detected, activeTab:', activeTab)
    }
  }, [adminType, activeTab])

  // Управление пользователями
  const [users, setUsers] = useState<User[]>([])
  const [usersLoading, setUsersLoading] = useState(false)

  // Управление зданиями
  const [buildings, setBuildings] = useState<Building[]>([])
  const [buildingsLoading, setBuildingsLoading] = useState(false)

  // Управление центрами
  const [centers, setCenters] = useState<Center[]>([])
  const [centersLoading, setCentersLoading] = useState(false)

  useEffect(() => {
    if (user && adminType) {  // Добавляем проверку adminType
      loadTicketsData()
      if (adminType === 'spravce_it') {
        loadUsersData()
        loadBuildingsData()
        loadCentersData()
      } else if (adminType === 'spravce_budov') {
        loadUsersData()
        loadBuildingsData()
      }
    }
  }, [user, adminType])

  const loadTicketsData = async () => {
    if (!user) return

    try {
      setLoading(true)
      const data = await loadTickets(user.id, user.role ?? null, user.admin_type)
      setAllTickets(data)
    } catch (error) {
      console.error('Chyba při načítání žádostí:', error)
      alert('Chyba při načítání žádostí')
    } finally {
      setLoading(false)
    }
  }

  useDeepLinkById({
    items: allTickets,
    ready: !loading,
    onFound: (t) => {
      setActiveTab('tickets')
      setHighlightId(t.id)
      if (t.status === 'new' || t.status === 'in_progress' || t.status === 'completed' || t.status === 'rejected') {
        setStatusFilter(t.status)
      } else {
        setStatusFilter(null)
      }
      // Otevřít přiřazení řešitele u nových
      if (t.status === 'new') setSelectedTicket(t)
    },
    onNotFound: () => showAlert('Žádost nenalezena nebo již není ve vaší frontě.', 'error'),
    elementIdFor: (id) => `ticket-${id}`,
  })

  const loadUsersData = async () => {
    try {
      setUsersLoading(true)
      const data = await loadUsers({ includeStudents: true })
      setUsers(data)
    } catch (error) {
      console.error('Chyba při načítání uživatelů:', error)
      alert('Chyba při načítání uživatelů')
    } finally {
      setUsersLoading(false)
    }
  }

  const loadBuildingsData = async () => {
    try {
      setBuildingsLoading(true)
      const data = await loadBuildings()
      setBuildings(data)
    } catch (error) {
      console.error('Chyba při načítání budov:', error)
      alert('Chyba při načítání budov')
    } finally {
      setBuildingsLoading(false)
    }
  }

  const loadCentersData = async () => {
    try {
      setCentersLoading(true)
      const data = await loadCenters()
      setCenters(data)
    } catch (error) {
      console.error('Chyba při načítání středisek:', error)
      alert('Chyba při načítání středisek')
    } finally {
      setCentersLoading(false)
    }
  }

  const handleAssignExecutor = async (ticketId: string, executorId: string, comment?: string) => {
    try {
      await updateTicketStatus(ticketId, 'in_progress', executorId, undefined, comment)
      await loadTicketsData()
      setSelectedTicket(null)
    } catch (error: any) {
      console.error('Chyba při přiřazování řešitele:', error)
      alert(error.message || 'Chyba při přiřazování řešitele')
    }
  }

  const handleAccelerate = async (ticketId: string) => {
    if (!confirm('Opravdu chcete požádat o urychlení této žádosti?')) {
      return
    }

    try {
      const result = await accelerateTicket(ticketId)
      alert(`Žádost o urychlení byla odeslána. Email byl odeslán ${result.emails_sent} ${result.emails_sent === 1 ? 'příjemci' : 'příjemcům'}.`)
    } catch (error: any) {
      console.error('Chyba při žádosti o urychlení:', error)
      alert(error.message || 'Chyba při žádosti o urychlení')
    }
  }

  const handleAddComment = async (ticketId: string, comment: string) => {
    try {
      await addTicketComment(ticketId, comment)
      await loadTicketsData()
    } catch (error: any) {
      console.error('Chyba při přidávání komentáře:', error)
      throw error
    }
  }

  // Фильтруем žádosti v závislosti na typu administrátora
  const tickets = useMemo(() => {
    if (adminType === 'spravce_it') {
      // Správce IT видí všechny žádosti
      return allTickets
    }
    return allTickets.filter((t) => t.admin_type === 'spravce_budov')
  }, [allTickets, adminType])

  const myTickets = useMemo(() => {
    if (adminType !== 'spravce_it') return []
    return tickets.filter(t => t.admin_type === 'spravce_it')
  }, [tickets, adminType])

  const buildingAdminTickets = useMemo(() => {
    if (adminType !== 'spravce_it') return []
    return tickets.filter((t) => t.admin_type === 'spravce_budov')
  }, [tickets, adminType])

  // Тикеты, отфильтрованные только по типу (без статуса) - для статистических карточек
  const ticketsByType = useMemo(() => {
    if (adminType === 'spravce_it' && ticketTypeFilter !== 'all') {
      if (ticketTypeFilter === 'my') {
        return myTickets
      } else if (ticketTypeFilter === 'building_admin') {
        return buildingAdminTickets
      }
    }
    return tickets
  }, [tickets, ticketTypeFilter, adminType, myTickets, buildingAdminTickets])

  // Фильтрация тикетов по выбранному статусу и типу
  const filteredTickets = useMemo(() => {
    let filtered = ticketsByType

    // Фильтр по статусу
    if (statusFilter) {
      filtered = filtered.filter(t => t.status === statusFilter)
    }

    // Фильтрация по поиску
    if (ticketsSearchText.trim()) {
      const searchLower = ticketsSearchText.toLowerCase().trim()
      filtered = filtered.filter(t => {
        const description = (t.description || '').toLowerCase()
        const requesterName = (t.requester_name || '').toLowerCase()
        const buildingName = (t.building_name || '').toLowerCase()
        const executorName = (t.executor_name || '').toLowerCase()
        const phone = (t.phone || '').toLowerCase()
        const requesterEmail = (t.requester_email || '').toLowerCase()
        const status = t.status.toLowerCase()

        return description.includes(searchLower) ||
          requesterName.includes(searchLower) ||
          buildingName.includes(searchLower) ||
          executorName.includes(searchLower) ||
          phone.includes(searchLower) ||
          requesterEmail.includes(searchLower) ||
          status.includes(searchLower)
      })
    }

    return filtered
  }, [ticketsByType, statusFilter, ticketsSearchText])

  const newTickets = filteredTickets.filter(t => t.status === 'new')
  const inProgressTickets = filteredTickets.filter(t => t.status === 'in_progress')
  const completedTickets = filteredTickets.filter(t => t.status === 'completed')

  // Пагинация для каждого списка
  const getPaginatedTickets = (tickets: Ticket[], page: number) => {
    const startIndex = (page - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return tickets.slice(startIndex, endIndex)
  }

  const getTotalPages = (tickets: Ticket[]) => {
    return Math.ceil(tickets.length / itemsPerPage)
  }

  // Определяем, какой список отображается для пагинации
  const getCurrentTicketsList = () => {
    if (statusFilter === 'new') return newTickets
    if (statusFilter === 'in_progress') return inProgressTickets
    if (statusFilter === 'completed') return completedTickets
    return filteredTickets
  }

  const currentTicketsList = getCurrentTicketsList()
  const paginatedTickets = viewMode === 'table' ? getPaginatedTickets(currentTicketsList, currentPage) : currentTicketsList
  const totalPages = getTotalPages(currentTicketsList)

  // Сбрасываем страницу при изменении фильтров
  useEffect(() => {
    setCurrentPage(1)
  }, [statusFilter, ticketTypeFilter, viewMode, ticketsSearchText])

  const handleAddUser = async (newUser: Omit<User, 'id'>) => {
    try {
      const result = await addUser(newUser.email, newUser.name, newUser.role as 'admin' | 'executor' | 'director' | 'financist' | 'referent' | 'vedouci' | 'vedouci_executor' | 'zastupce_reditele', newUser.admin_type)
      await loadUsersData()
      // Показываем соответствующее сообщение в зависимости от того, был ли пользователь создан или обновлен
      if (result.updated) {
        alert(`Uživatel ${newUser.name} již existoval a jeho role byla aktualizována`)
      } else {
        alert(`Uživatel ${newUser.name} byl přidán`)
      }
    } catch (error: any) {
      console.error('Chyba při přidávání uživatele:', error)
      // Если ошибка связана с правами доступа, показываем более понятное сообщение
      if (error.message && (error.message.includes('nemůže') || error.message.includes('cannot') || error.message.includes('přiřazovat'))) {
        alert('Nemáte oprávnění upravit tohoto uživatele. ' + (error.message.includes('přiřazovat') ? 'Správce budov může přiřazovat roli řešitele pouze uživatelům bez role nebo s rolí žadatele.' : error.message))
      } else {
        alert(error.message || 'Chyba při přidávání uživatele')
      }
    }
  }

  const handleUpdateUser = async (userId: string, updatedUser: Omit<User, 'id'>) => {
    try {
      console.log('Updating user:', userId, 'with role:', updatedUser.role)
      await updateUser(
        userId,
        updatedUser.name,
        updatedUser.role as 'admin' | 'executor' | 'director' | 'financist' | 'referent' | 'vedouci' | 'vedouci_executor' | 'property_referent' | 'spravce_rozvrhu' | 'spravce_jidelny' | 'zastupce_reditele',
        updatedUser.admin_type
      )
      // Небольшая задержка перед перезагрузкой, чтобы убедиться, что данные сохранились
      await new Promise(resolve => setTimeout(resolve, 200))

      // Проверяем, что роль обновилась - сначала из ответа API
      console.log('Checking updated user from API response...')

      // Загружаем пользователей заново
      await loadUsersData()

      // Проверяем, что роль обновилась
      const updatedUsers = await loadUsers({ includeStudents: true })
      console.log('All users after update:', updatedUsers.map(u => ({ id: u.id, name: u.name, role: u.role })))
      const user = updatedUsers.find(u => u.id === userId)
      console.log('User after update:', user?.name, 'Role:', user?.role)
      console.log('Full user object:', user)
      alert(`Uživatel ${updatedUser.name} byl úspěšně aktualizován`)
    } catch (error: any) {
      console.error('Chyba při aktualizaci uživatele:', error)
      if (error.message && (error.message.includes('nemůže') || error.message.includes('cannot') || error.message.includes('přiřazovat'))) {
        alert('Nemáte oprávnění upravit tohoto uživatele. ' + (error.message.includes('přiřazovat') ? 'Správce budov může přiřazovat roli řešitele pouze uživatelům bez role nebo s rolí žadatele.' : error.message))
      } else {
        alert(error.message || 'Chyba při aktualizaci uživatele')
      }
    }
  }

  const handleDeleteUser = async (userId: string) => {
    if (!confirm('Opravdu chcete odstranit tohoto uživatele?')) {
      return
    }

    try {
      await deleteUser(userId)
      await loadUsersData()
      alert('Uživatel byl odstraněn')
    } catch (error: any) {
      console.error('Chyba při mazání uživatele:', error)
      alert(error.message || 'Chyba při mazání uživatele')
    }
  }

  const handleAddBuilding = async (buildingName: string) => {
    try {
      await addBuilding(buildingName)
      await loadBuildingsData()
      alert(`Budova ${buildingName} byla přidána`)
    } catch (error: any) {
      console.error('Chyba při přidávání budovy:', error)
      alert(error.message || 'Chyba při přidávání budovy')
    }
  }

  const handleDeleteBuilding = async (buildingId: string) => {
    const building = buildings.find(b => b.id === buildingId)
    if (!confirm(`Opravdu chcete odstranit budovu ${building?.name}?`)) {
      return
    }

    try {
      await deleteBuilding(buildingId)
      await loadBuildingsData()
      alert(`Budova ${building?.name} byla odstraněna`)
    } catch (error: any) {
      console.error('Chyba při mazání budovy:', error)
      alert(error.message || 'Chyba při mazání budovy')
    }
  }

  const handleAddCenter = async (centerName: string) => {
    try {
      await addCenter(centerName)
      await loadCentersData()
      alert(`Středisko ${centerName} bylo přidáno`)
    } catch (error: any) {
      console.error('Chyba při přidávání střediska:', error)
      alert(error.message || 'Chyba při přidávání střediska')
    }
  }

  const handleDeleteCenter = async (centerId: string) => {
    const center = centers.find(c => c.id === centerId)
    if (!confirm(`Opravdu chcete odstranit středisko ${center?.name}?`)) {
      return
    }

    try {
      await deleteCenter(centerId)
      await loadCentersData()
      alert(`Středisko ${center?.name} bylo odstraněno`)
    } catch (error: any) {
      console.error('Chyba při mazání střediska:', error)
      alert(error.message || 'Chyba při mazání střediska')
    }
  }


  const handleImportCSV = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (!file.name.endsWith('.csv')) {
      alert('Prosím vyberte CSV soubor')
      event.target.value = ''
      return
    }

    if (!confirm(`Opravdu chcete importovat uživatele z CSV souboru "${file.name}"?\n\nPoznámka: Existující uživatelé budou aktualizováni (pouze jméno). Noví uživatelé budou vytvořeni s rolí "requester".\n\nRole a admin_type se NEMĚNÍ.`)) {
      event.target.value = ''
      return
    }

    try {
      setLoading(true)
      const result = await importUsersFromCSV(file)

      if (result.success) {
        alert(
          `Import dokončen!\n\n` +
          `Vytvořeno: ${result.created || 0}\n` +
          `Aktualizováno: ${result.updated || 0}\n` +
          `Přeskočeno: ${result.skipped || 0}\n` +
          (result.errors && result.errors.length > 0 ? `\nChyby: ${result.errors.length}` : '') +
          `\n\nCelkem zpracováno: ${result.total_processed || 0} uživatelů`
        )
        await loadUsersData()
      } else {
        alert('Chyba při importu: ' + (result.error || 'Neznámá chyba'))
      }
    } catch (error: any) {
      console.error('Chyba při importu uživatelů:', error)
      alert(error.message || 'Chyba při importu uživatelů z CSV')
    } finally {
      setLoading(false)
      event.target.value = ''
    }
  }

  // Обработчик удаления заявки (ticket)
  const handleDeleteTicket = async (ticketId: string) => {
    if (!confirm('Opravdu chcete smazat tuto žádost? Tato akce je nevratná.')) {
      return
    }

    try {
      await deleteTicket(ticketId)
      await loadTicketsData()
      alert('Žádost byla smazána')
    } catch (error: any) {
      console.error('Chyba při mazání žádosti:', error)
      alert(error.message || 'Chyba při mazání žádosti')
    }
  }

  // Загрузка заявок на покупки
  const loadPurchaseRequestsData = async () => {
    if (!user) return

    try {
      setPurchaseRequestsLoading(true)
      const data = await loadPurchaseRequests(user.id, user.role ?? null, null)
      setPurchaseRequests(data)
    } catch (error) {
      console.error('Chyba při načítání žádostí o nákup:', error)
    } finally {
      setPurchaseRequestsLoading(false)
    }
  }

  // Обработчик удаления заявки на покупку
  const handleDeletePurchaseRequest = async (requestId: string) => {
    if (!confirm('Opravdu chcete smazat tuto žádost o nákup? Tato akce je nevratná.')) {
      return
    }

    try {
      await deletePurchaseRequest(requestId)
      await loadPurchaseRequestsData()
      alert('Žádost o nákup byla smazána')
    } catch (error: any) {
      console.error('Chyba při mazání žádosti o nákup:', error)
      alert(error.message || 'Chyba při mazání žádosti o nákup')
    }
  }

  // Загружаем заявки на покупки при открытии соответствующей вкладки
  useEffect(() => {
    if (activeTab === 'settings' && settingsSubTab === 'purchase_requests' && (adminType === 'spravce_it' || adminType === 'spravce_budov')) {
      loadPurchaseRequestsData()
    }
  }, [activeTab, settingsSubTab, adminType, user])

  // Фильтрация заявок на покупки по тексту поиска и статусу
  const filteredPurchaseRequests = useMemo(() => {
    let filtered = purchaseRequests

    if (purchaseRequestsStatusFilter !== 'all') {
      filtered = filtered.filter((r: any) => r.status === purchaseRequestsStatusFilter)
    }

    if (!purchaseRequestsSearchText.trim()) {
      return filtered
    }

    const searchLower = purchaseRequestsSearchText.toLowerCase().trim()
    return filtered.filter((request: any) => {
      const subject = (request.subject || '').toLowerCase()
      const description = (request.description || '').toLowerCase()
      const reason = (request.reason || '').toLowerCase()
      const supplier = (request.supplier || '').toLowerCase()
      const centerName = (request.center_name || '').toLowerCase()
      const createdByName = (request.created_by_name || '').toLowerCase()
      const notes = (request.notes || '').toLowerCase()

      return subject.includes(searchLower) ||
        description.includes(searchLower) ||
        reason.includes(searchLower) ||
        supplier.includes(searchLower) ||
        centerName.includes(searchLower) ||
        createdByName.includes(searchLower) ||
        notes.includes(searchLower)
    })
  }, [purchaseRequests, purchaseRequestsSearchText, purchaseRequestsStatusFilter])

  const paginatedPurchaseRequests = useMemo(() => {
    const startIndex = (purchaseRequestsCurrentPage - 1) * purchaseRequestsItemsPerPage
    const endIndex = startIndex + purchaseRequestsItemsPerPage
    return filteredPurchaseRequests.slice(startIndex, endIndex)
  }, [filteredPurchaseRequests, purchaseRequestsCurrentPage, purchaseRequestsItemsPerPage])

  const purchaseRequestsTotalPages = useMemo(() => {
    return Math.ceil(filteredPurchaseRequests.length / purchaseRequestsItemsPerPage)
  }, [filteredPurchaseRequests, purchaseRequestsItemsPerPage])

  const purchaseRequestsUniqueStatuses = useMemo(() => {
    const statuses = new Set(purchaseRequests.map((r: any) => r.status))
    return Array.from(statuses)
  }, [purchaseRequests])

  const getPurchaseRequestStatusLabel = (status: string) => {
    switch (status) {
      case 'pending_leader': return 'Čeká na vedoucího'
      case 'new': return 'Nová'
      case 'deferred': return 'Odloženo'
      case 'rejected_by_economist': return 'Zamítnuto ekonomem'
      case 'approved_by_economist': return 'Schváleno ekonomem'
      case 'approved_by_director': return 'Schváleno ředitelem'
      case 'rejected_by_leader': return 'Zamítnuto vedoucím'
      case 'cancelled_by_requester': return 'Zrušeno žadatelem'
      case 'cancelled_by_leader': return 'Zrušeno vedoucím'
      case 'accepted_by_referent': return 'Přijato referentem'
      case 'ordered_by_referent': return 'Objednáno referentem'
      case 'ready_by_referent': return 'Připraveno referentem'
      case 'completed': return 'Dokončeno'
      default: return status
    }
  }

  // Загрузка заявок на перемещение маетка
  const loadPropertyMoveRequestsData = async () => {
    try {
      setPropertyMoveRequestsLoading(true)
      const data = await loadPropertyMoveTickets()
      setPropertyMoveRequests(data)
    } catch (error) {
      console.error('Chyba při načítání žádostí o přesun majetku:', error)
    } finally {
      setPropertyMoveRequestsLoading(false)
    }
  }

  const handleDeletePropertyMoveRequest = async (requestId: string) => {
    if (!confirm('Opravdu chcete smazat tuto žádost o přesun majetku? Tato akce je nevratná.')) {
      return
    }

    try {
      await deletePropertyMoveTicket(requestId)
      await loadPropertyMoveRequestsData()
      alert('Žádost o přesun majetku byla smazána')
    } catch (error: any) {
      console.error('Chyba při mazání žádosti o přesun majetku:', error)
      alert(error.message || 'Chyba při mazání žádosti o přesun majetku')
    }
  }

  // Загружаем заявки на перемещение для IT администратора
  useEffect(() => {
    if (activeTab === 'settings' && settingsSubTab === 'property_move_requests' && adminType === 'spravce_it') {
      loadPropertyMoveRequestsData()
    }
  }, [activeTab, settingsSubTab, adminType, user])

  // Фильтрация заявок на перемещение маетка
  const filteredPropertyMoveRequests = useMemo(() => {
    let filtered = propertyMoveRequests

    if (propertyMoveRequestsStatusFilter !== 'all') {
      filtered = filtered.filter((r: any) => r.status === propertyMoveRequestsStatusFilter)
    }

    if (!propertyMoveRequestsSearchText.trim()) {
      return filtered
    }

    const searchLower = propertyMoveRequestsSearchText.toLowerCase().trim()
    return filtered.filter((r: any) => {
      const inventoryNumber = (r.inventory_number || '').toLowerCase()
      const itemName = (r.item_name || '').toLowerCase()
      const fromLocation = (r.from_location || '').toLowerCase()
      const toLocation = (r.to_location || '').toLowerCase()
      const assignedToName = (r.assigned_to_name || '').toLowerCase()
      const requesterName = (r.requester_name || '').toLowerCase()
      const fromBuildingName = (r.from_building_name || '').toLowerCase()
      const toBuildingName = (r.to_building_name || '').toLowerCase()
      const status = (r.status || '').toLowerCase()

      return inventoryNumber.includes(searchLower) ||
        itemName.includes(searchLower) ||
        fromLocation.includes(searchLower) ||
        toLocation.includes(searchLower) ||
        assignedToName.includes(searchLower) ||
        requesterName.includes(searchLower) ||
        fromBuildingName.includes(searchLower) ||
        toBuildingName.includes(searchLower) ||
        status.includes(searchLower)
    })
  }, [propertyMoveRequests, propertyMoveRequestsSearchText, propertyMoveRequestsStatusFilter])

  const paginatedPropertyMoveRequests = useMemo(() => {
    const startIndex = (propertyMoveRequestsCurrentPage - 1) * propertyMoveRequestsItemsPerPage
    const endIndex = startIndex + propertyMoveRequestsItemsPerPage
    return filteredPropertyMoveRequests.slice(startIndex, endIndex)
  }, [filteredPropertyMoveRequests, propertyMoveRequestsCurrentPage, propertyMoveRequestsItemsPerPage])

  const propertyMoveRequestsTotalPages = useMemo(() => {
    return Math.ceil(filteredPropertyMoveRequests.length / propertyMoveRequestsItemsPerPage)
  }, [filteredPropertyMoveRequests, propertyMoveRequestsItemsPerPage])

  const propertyMoveRequestsUniqueStatuses = useMemo(() => {
    const statuses = new Set(propertyMoveRequests.map((r: any) => r.status))
    return Array.from(statuses)
  }, [propertyMoveRequests])

  const getPropertyMoveRequestStatusLabel = (status: string) => {
    switch (status) {
      case 'new': return 'Nová'
      case 'in_progress': return 'V řešení'
      case 'completed': return 'Dokončeno'
      case 'rejected': return 'Zamítnuto'
      case 'awaiting_recipient_response': return 'Čeká na odpověď příjemce'
      case 'recipient_accepted_self_transport': return 'Příjemce přijal - vlastní přesun'
      case 'recipient_needs_transport': return 'Příjemce potřebuje přesun'
      case 'awaiting_transport': return 'Čeká na přesun'
      default: return status
    }
  }

  return (
    <div className="admin-view">
      <div className="admin-info">
        <p>
          <strong>Typ administrátora:</strong>{' '}
          <span className="admin-type-badge">
            {adminType === 'spravce_it' ? 'Správce IT' : 'Správce Budov'}
          </span>
          {adminType === 'spravce_it' && (
            <span style={{ marginLeft: '1rem', color: '#666', fontSize: '0.9rem' }}>
              (Zobrazují se všechny žádosti)
            </span>
          )}
          {adminType === 'spravce_budov' && (
            <span style={{ marginLeft: '1rem', color: '#666', fontSize: '0.9rem' }}>
              (Zobrazují se všechny žádosti pro správce budov)
            </span>
          )}
        </p>
      </div>
      {(adminType === 'spravce_it' || adminType === 'spravce_budov') && (
        <div className="admin-tabs">
          <button
            className={`tab-button ${activeTab === 'tickets' ? 'active' : ''}`}
            onClick={() => setActiveTab('tickets')}
          >
            📋 Žádosti
          </button>
          <button
            className={`tab-button ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('settings')
              // Устанавливаем первый доступный подраздел при открытии настроек
              if (adminType === 'spravce_it') {
                setSettingsSubTab('system')
              } else if (adminType === 'spravce_budov') {
                setSettingsSubTab('buildings')
              }
            }}
          >
            ⚙️ Nastavení
          </button>
          <button
            className={`tab-button ${activeTab === 'notes' ? 'active' : ''}`}
            onClick={() => setActiveTab('notes')}
          >
            📝 Poznámky
          </button>
          {adminType === 'spravce_budov' && (
            <button
              className="tab-button statistics-button"
              onClick={() => setShowStatistics(true)}
              style={{
                background: '#14b8a6',
                color: 'white',
                border: 'none'
              }}
            >
              📊 Statistika
            </button>
          )}
          {(adminType === 'spravce_it' || user?.role === 'director') && (
            <button
              className="tab-button"
              onClick={() => window.location.href = '/public-canteen/statistics'}
              style={{
                background: '#667eea',
                color: 'white',
                border: 'none'
              }}
            >
              📊 Statistiky jídelny
            </button>
          )}
        </div>
      )}

      {activeTab === 'tickets' && (
        <>
          {adminType === 'spravce_it' && (
            <div className="ticket-type-filter" style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <button
                className={`filter-button ${ticketTypeFilter === 'all' ? 'active' : ''}`}
                onClick={() => setTicketTypeFilter('all')}
                style={{
                  padding: '0.5rem 1rem',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  background: ticketTypeFilter === 'all' ? '#14b8a6' : 'white',
                  color: ticketTypeFilter === 'all' ? 'white' : '#333',
                  cursor: 'pointer'
                }}
              >
                Všechny
              </button>
              <button
                className={`filter-button ${ticketTypeFilter === 'my' ? 'active' : ''}`}
                onClick={() => setTicketTypeFilter('my')}
                style={{
                  padding: '0.5rem 1rem',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  background: ticketTypeFilter === 'my' ? '#14b8a6' : 'white',
                  color: ticketTypeFilter === 'my' ? 'white' : '#333',
                  cursor: 'pointer'
                }}
              >
                Moje (IT)
              </button>
              <button
                className={`filter-button ${ticketTypeFilter === 'building_admin' ? 'active' : ''}`}
                onClick={() => setTicketTypeFilter('building_admin')}
                style={{
                  padding: '0.5rem 1rem',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  background: ticketTypeFilter === 'building_admin' ? '#ffc107' : 'white',
                  color: ticketTypeFilter === 'building_admin' ? 'white' : '#333',
                  cursor: 'pointer'
                }}
              >
                Správce Budov
              </button>
            </div>
          )}
          <div className="admin-stats">
            <div
              className={`stat-card ${statusFilter === 'new' ? 'active' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'new' ? null : 'new')}
              style={{ cursor: 'pointer' }}
            >
              <h3>Nové žádosti</h3>
              <p className="stat-number">{ticketsByType.filter(t => t.status === 'new').length}</p>
            </div>
            <div
              className={`stat-card ${statusFilter === 'in_progress' ? 'active' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'in_progress' ? null : 'in_progress')}
              style={{ cursor: 'pointer' }}
            >
              <h3>V řešení</h3>
              <p className="stat-number">{ticketsByType.filter(t => t.status === 'in_progress').length}</p>
            </div>
            <div
              className={`stat-card ${statusFilter === 'completed' ? 'active' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'completed' ? null : 'completed')}
              style={{ cursor: 'pointer' }}
            >
              <h3>Dokončeno</h3>
              <p className="stat-number">{ticketsByType.filter(t => t.status === 'completed').length}</p>
            </div>
            <div
              className={`stat-card ${statusFilter === null ? 'active' : ''}`}
              onClick={() => setStatusFilter(null)}
              style={{ cursor: 'pointer' }}
            >
              <h3>Celkem</h3>
              <p className="stat-number">{ticketsByType.length}</p>
            </div>
          </div>

          <div className="view-controls" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                className={`view-button ${viewMode === 'cards' ? 'active' : ''}`}
                onClick={() => setViewMode('cards')}
              >
                📋 Karty
              </button>
              <button
                className={`view-button ${viewMode === 'table' ? 'active' : ''}`}
                onClick={() => setViewMode('table')}
              >
                📊 Tabulka
              </button>
            </div>
            <div className="search-input-container" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flex: 1, minWidth: '200px', maxWidth: '400px' }}>
              <input
                type="text"
                placeholder="Hledat v žádostech..."
                value={ticketsSearchText}
                onChange={(e) => setTicketsSearchText(e.target.value)}
                className="search-input"
                style={{ flex: 1, padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
              />
              {ticketsSearchText && (
                <button className="clear-search-button" onClick={() => setTicketsSearchText('')} style={{ padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: 'white', cursor: 'pointer' }}>
                  ✕
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="loading">Načítání...</div>
          ) : viewMode === 'table' ? (
            <>
              <TicketsTable
                tickets={paginatedTickets}
                onAssignExecutor={(ticket) => setSelectedTicket(ticket)}
                onAccelerate={adminType === 'spravce_it' ? handleAccelerate : undefined}
                onDelete={adminType === 'spravce_it' ? handleDeleteTicket : undefined}
                onAddComment={handleAddComment}
                showAccelerateButton={adminType === 'spravce_it'}
                showDeleteButton={adminType === 'spravce_it'}
                showAddCommentButton={true}
                filterType="admin"
                adminType={adminType}
              />
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                itemsPerPage={itemsPerPage}
                totalItems={currentTicketsList.length}
              />
            </>
          ) : statusFilter ? (
            <>
              {statusFilter === 'new' && (
                <div className="tickets-section">
                  <h2>Nové žádosti ({newTickets.length})</h2>
                  {newTickets.length > 0 ? (
                    <>
                      <TicketList
                        highlightedTicketId={highlightId}
                        tickets={getPaginatedTickets(newTickets, currentPage)}
                        onAssignExecutor={(ticket) => setSelectedTicket(ticket)}
                        onAddComment={handleAddComment}
                        showAssignButton={true}
                        showAddCommentButton={true}
                        searchText={ticketsSearchText}
                      />
                      <Pagination
                        currentPage={currentPage}
                        totalPages={getTotalPages(newTickets)}
                        onPageChange={setCurrentPage}
                        itemsPerPage={itemsPerPage}
                        totalItems={newTickets.length}
                      />
                    </>
                  ) : (
                    <p style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>Žádné nové žádosti</p>
                  )}
                </div>
              )}
              {statusFilter === 'in_progress' && (
                <div className="tickets-section">
                  <h2>Žádosti v řešení ({inProgressTickets.length})</h2>
                  {inProgressTickets.length > 0 ? (
                    <>
                      <TicketList
                        highlightedTicketId={highlightId}
                        tickets={getPaginatedTickets(inProgressTickets, currentPage)}
                        onAssignExecutor={(ticket) => setSelectedTicket(ticket)}
                        showAssignButton={true}
                        searchText={ticketsSearchText}
                      />
                      <Pagination
                        currentPage={currentPage}
                        totalPages={getTotalPages(inProgressTickets)}
                        onPageChange={setCurrentPage}
                        itemsPerPage={itemsPerPage}
                        totalItems={inProgressTickets.length}
                      />
                    </>
                  ) : (
                    <p style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>Žádné žádosti v řešení</p>
                  )}
                </div>
              )}
              {statusFilter === 'completed' && (
                <div className="tickets-section">
                  <h2>Dokončené žádosti ({completedTickets.length})</h2>
                  {completedTickets.length > 0 ? (
                    <>
                      <TicketList
                        highlightedTicketId={highlightId}
                        tickets={getPaginatedTickets(completedTickets, currentPage)}
                        searchText={ticketsSearchText}
                      />
                      <Pagination
                        currentPage={currentPage}
                        totalPages={getTotalPages(completedTickets)}
                        onPageChange={setCurrentPage}
                        itemsPerPage={itemsPerPage}
                        totalItems={completedTickets.length}
                      />
                    </>
                  ) : (
                    <p style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>Žádné dokončené žádosti</p>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="tickets-section">
                <h2>Nové žádosti ({newTickets.length})</h2>
                {newTickets.length > 0 ? (
                  <>
                    <TicketList
                      highlightedTicketId={highlightId}
                      tickets={getPaginatedTickets(newTickets, currentPage)}
                      onAssignExecutor={(ticket) => setSelectedTicket(ticket)}
                      onAddComment={handleAddComment}
                      showAssignButton={true}
                      showAddCommentButton={true}
                      searchText={ticketsSearchText}
                    />
                    <Pagination
                      currentPage={currentPage}
                      totalPages={getTotalPages(newTickets)}
                      onPageChange={setCurrentPage}
                      itemsPerPage={itemsPerPage}
                      totalItems={newTickets.length}
                    />
                  </>
                ) : (
                  <p style={{ padding: '1rem', textAlign: 'center', color: '#666', fontStyle: 'italic' }}>Žádné nové žádosti</p>
                )}
              </div>

              <div className="tickets-section">
                <h2>Žádosti v řešení ({inProgressTickets.length})</h2>
                {inProgressTickets.length > 0 ? (
                  <>
                    <TicketList
                      highlightedTicketId={highlightId}
                      tickets={getPaginatedTickets(inProgressTickets, currentPage)}
                      onAssignExecutor={(ticket) => setSelectedTicket(ticket)}
                      showAssignButton={true}
                      searchText={ticketsSearchText}
                    />
                    <Pagination
                      currentPage={currentPage}
                      totalPages={getTotalPages(inProgressTickets)}
                      onPageChange={setCurrentPage}
                      itemsPerPage={itemsPerPage}
                      totalItems={inProgressTickets.length}
                    />
                  </>
                ) : (
                  <p style={{ padding: '1rem', textAlign: 'center', color: '#666', fontStyle: 'italic' }}>Žádné žádosti v řešení</p>
                )}
              </div>

              <div className="tickets-section">
                <h2>Dokončené žádosti ({completedTickets.length})</h2>
                {completedTickets.length > 0 ? (
                  <>
                    <TicketList highlightedTicketId={highlightId} tickets={getPaginatedTickets(completedTickets, currentPage)} searchText={ticketsSearchText} />
                    <Pagination
                      currentPage={currentPage}
                      totalPages={getTotalPages(completedTickets)}
                      onPageChange={setCurrentPage}
                      itemsPerPage={itemsPerPage}
                      totalItems={completedTickets.length}
                    />
                  </>
                ) : (
                  <p style={{ padding: '1rem', textAlign: 'center', color: '#666', fontStyle: 'italic' }}>Žádné dokončené žádosti</p>
                )}
              </div>
            </>
          )}
        </>
      )}

      {activeTab === 'notes' && (
        <NotesPanel />
      )}

      {activeTab === 'settings' && (
        <div className="settings-container">
          <div className="settings-subtabs">
            {adminType === 'spravce_it' && (
              <button
                className={`subtab-button ${settingsSubTab === 'users' ? 'active' : ''}`}
                onClick={() => setSettingsSubTab('users')}
              >
                👥 Uživatelé
              </button>
            )}
            <button
              className={`subtab-button ${settingsSubTab === 'buildings' ? 'active' : ''}`}
              onClick={() => setSettingsSubTab('buildings')}
            >
              🏢 Budovy
            </button>
            {adminType === 'spravce_budov' && (
              <button
                className={`subtab-button ${settingsSubTab === 'executors' ? 'active' : ''}`}
                onClick={() => setSettingsSubTab('executors')}
              >
                👷 Řešitelé
              </button>
            )}
            {adminType === 'spravce_it' && (
              <button
                className={`subtab-button ${settingsSubTab === 'centers' ? 'active' : ''}`}
                onClick={() => setSettingsSubTab('centers')}
              >
                🏛️ Střediska
              </button>
            )}
            {adminType === 'spravce_it' && (
              <button
                className={`subtab-button ${settingsSubTab === 'system' ? 'active' : ''}`}
                onClick={() => setSettingsSubTab('system')}
              >
                ⚙️ Systém
              </button>
            )}
            {adminType === 'spravce_it' && (
              <>
                <button
                  className={`subtab-button ${settingsSubTab === 'purchase_requests' ? 'active' : ''}`}
                  onClick={() => setSettingsSubTab('purchase_requests')}
                  style={{ display: (adminType === 'spravce_it' || adminType === 'spravce_budov') ? 'block' : 'none' }}
                >
                  🛒 Žádosti o nákup
                </button>
                <button
                  className={`subtab-button ${settingsSubTab === 'property_move_requests' ? 'active' : ''}`}
                  onClick={() => setSettingsSubTab('property_move_requests')}
                >
                  📦 Žádosti o přesun majetku
                </button>
              </>
            )}
            {(adminType === 'spravce_it' || adminType === 'spravce_budov') && (
              <button
                className={`subtab-button ${settingsSubTab === 'suppliers' ? 'active' : ''}`}
                onClick={() => setSettingsSubTab('suppliers')}
              >
                📋 Dodavatelé
              </button>
            )}
            {/* Кнопка IT Informace - видна только для IT администраторов */}
            {adminType === 'spravce_it' && (
              <button
                className={`subtab-button ${settingsSubTab === 'it_admin_info' ? 'active' : ''}`}
                onClick={() => {
                  console.log('IT Informace button clicked, adminType:', adminType)
                  setSettingsSubTab('it_admin_info')
                }}
              >
                📝 IT Informace
              </button>
            )}
            {/* Кнопка Novinky týdne - видна для IT администраторов и Správce Budov */}
            {(adminType === 'spravce_it' || adminType === 'spravce_budov') && (
              <button
                className={`subtab-button ${settingsSubTab === 'weekly_news' ? 'active' : ''}`}
                onClick={() => {
                  setSettingsSubTab('weekly_news')
                }}
              >
                📰 Novinky týdne
              </button>
            )}
            {/* Кнопка Seznam zaměstnanců - видна только для IT администраторов */}
            {adminType === 'spravce_it' && (
              <button
                className={`subtab-button ${settingsSubTab === 'school_employees' ? 'active' : ''}`}
                onClick={() => {
                  setSettingsSubTab('school_employees')
                }}
                style={{
                  display: 'block'
                }}
              >
                👥 Seznam zaměstnanců
              </button>
            )}
            {adminType === 'spravce_it' && (
              <button
                className={`subtab-button ${settingsSubTab === 'module_access' ? 'active' : ''}`}
                onClick={() => setSettingsSubTab('module_access')}
              >
                🔧 Moduly (přístup)
              </button>
            )}
            {/* Кнопка Бэкап - видна только для IT администраторов */}
            {adminType === 'spravce_it' && (
              <button
                className={`subtab-button ${settingsSubTab === 'backup' ? 'active' : ''}`}
                onClick={() => {
                  setSettingsSubTab('backup')
                }}
              >
                💾 Бэкап
              </button>
            )}
          </div>

          <div className="settings-content">
            {settingsSubTab === 'users' && (
              adminType === 'spravce_it' ? (
                <UserManagement
                  users={users}
                  loading={usersLoading}
                  onAddUser={handleAddUser}
                  onUpdateUser={handleUpdateUser}
                  onDeleteUser={handleDeleteUser}
                  onRefresh={loadUsersData}
                />
              ) : (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                  <p>Přístup pouze pro správce IT</p>
                </div>
              )
            )}

            {settingsSubTab === 'buildings' && (
              <BuildingManagement
                buildings={buildings}
                loading={buildingsLoading}
                onAddBuilding={handleAddBuilding}
                onDeleteBuilding={handleDeleteBuilding}
                onRefresh={loadBuildingsData}
              />
            )}

            {settingsSubTab === 'executors' && adminType === 'spravce_budov' && (
              <ExecutorRoleManagement
                users={users}
                loading={usersLoading}
                onRefresh={loadUsersData}
              />
            )}

            {settingsSubTab === 'suppliers' && (adminType === 'spravce_it' || adminType === 'spravce_budov') && (
              <SuppliersManagement />
            )}


            {settingsSubTab === 'centers' && (
              adminType === 'spravce_it' ? (
                <CenterManagement
                  centers={centers}
                  loading={centersLoading}
                  onAddCenter={handleAddCenter}
                  onDeleteCenter={handleDeleteCenter}
                  onRefresh={loadCentersData}
                />
              ) : (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                  <p>Přístup pouze pro správce IT</p>
                </div>
              )
            )}

            {settingsSubTab === 'purchase_requests' && (adminType === 'spravce_it' || adminType === 'spravce_budov') && (
              <div className="settings-section">
                <h2>Správa žádostí o nákup</h2>
                {purchaseRequestsLoading ? (
                  <div className="loading">Načítání...</div>
                ) : (
                  <div className="purchase-requests-management">
                    {/* Фильтры и переключение режима просмотра */}
                    <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: '200px' }}>
                        <input
                          type="text"
                          placeholder="Hledat podle textu..."
                          value={purchaseRequestsSearchText}
                          onChange={(e) => setPurchaseRequestsSearchText(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            fontSize: '1rem',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                      <div style={{ minWidth: '200px' }}>
                        <select
                          value={purchaseRequestsStatusFilter}
                          onChange={(e) => setPurchaseRequestsStatusFilter(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            fontSize: '1rem',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            boxSizing: 'border-box'
                          }}
                        >
                          <option value="all">Všechny statusy</option>
                          {purchaseRequestsUniqueStatuses.map(status => (
                            <option key={status} value={status}>
                              {getPurchaseRequestStatusLabel(status)}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          className={`view-button ${purchaseRequestsViewMode === 'cards' ? 'active' : ''}`}
                          onClick={() => setPurchaseRequestsViewMode('cards')}
                          style={{
                            padding: '0.5rem 1rem',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            backgroundColor: purchaseRequestsViewMode === 'cards' ? '#14b8a6' : 'white',
                            color: purchaseRequestsViewMode === 'cards' ? 'white' : '#333',
                            cursor: 'pointer'
                          }}
                        >
                          Karty
                        </button>
                        <button
                          className={`view-button ${purchaseRequestsViewMode === 'table' ? 'active' : ''}`}
                          onClick={() => setPurchaseRequestsViewMode('table')}
                          style={{
                            padding: '0.5rem 1rem',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            backgroundColor: purchaseRequestsViewMode === 'table' ? '#14b8a6' : 'white',
                            color: purchaseRequestsViewMode === 'table' ? 'white' : '#333',
                            cursor: 'pointer'
                          }}
                        >
                          Tabulka
                        </button>
                      </div>
                      {purchaseRequestsSearchText && (
                        <button
                          onClick={() => setPurchaseRequestsSearchText('')}
                          style={{
                            padding: '0.5rem 1rem',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            backgroundColor: 'white',
                            cursor: 'pointer'
                          }}
                        >
                          ✕
                        </button>
                      )}
                    </div>

                    {filteredPurchaseRequests.length === 0 ? (
                      <p style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                        {purchaseRequestsSearchText || purchaseRequestsStatusFilter !== 'all' ? 'Žádné žádosti neodpovídají filtrům' : 'Žádné žádosti o nákup'}
                      </p>
                    ) : purchaseRequestsViewMode === 'cards' ? (
                      <>
                        <div className="requests-list" style={{ display: 'grid', gap: '1rem' }}>
                          {paginatedPurchaseRequests.map((request: any) => (
                            <PurchaseRequestCard
                              key={request.id}
                              request={request}
                              searchText={purchaseRequestsSearchText}
                            />
                          ))}
                        </div>
                        {purchaseRequestsTotalPages > 1 && (
                          <Pagination
                            currentPage={purchaseRequestsCurrentPage}
                            totalPages={purchaseRequestsTotalPages}
                            onPageChange={setPurchaseRequestsCurrentPage}
                            itemsPerPage={purchaseRequestsItemsPerPage}
                            totalItems={filteredPurchaseRequests.length}
                          />
                        )}
                      </>
                    ) : (
                      <>
                        <table className="purchase-requests-admin-table">
                          <thead>
                            <tr>
                              <th>Předmět</th>
                              <th>Středisko</th>
                              <th>Vytvořil</th>
                              <th>Částka</th>
                              <th>Datum vytvoření</th>
                              <th>Akce</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedPurchaseRequests.map((request: any) => (
                              <tr key={request.id}>
                                <td>{highlightTextMultiple(request.subject, purchaseRequestsSearchText)}</td>
                                <td>{highlightTextMultiple(request.center_name || '-', purchaseRequestsSearchText)}</td>
                                <td>{highlightTextMultiple(request.created_by_name || '-', purchaseRequestsSearchText)}</td>
                                <td>{request.amount ? `${request.amount.toLocaleString('cs-CZ')} Kč` : '-'}</td>
                                <td>{new Date(request.created_at).toLocaleString('cs-CZ')}</td>
                                <td>
                                  <button
                                    className="btn-delete-small delete-button"
                                    onClick={() => handleDeletePurchaseRequest(request.id)}
                                  >
                                    🗑️ Smazat
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {purchaseRequestsTotalPages > 1 && (
                          <Pagination
                            currentPage={purchaseRequestsCurrentPage}
                            totalPages={purchaseRequestsTotalPages}
                            onPageChange={setPurchaseRequestsCurrentPage}
                            itemsPerPage={purchaseRequestsItemsPerPage}
                            totalItems={filteredPurchaseRequests.length}
                          />
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
            {settingsSubTab === 'property_move_requests' && adminType === 'spravce_it' && (
              <div className="settings-section">
                <h2>Správa žádostí o přesun majetku</h2>
                {propertyMoveRequestsLoading ? (
                  <div className="loading">Načítání...</div>
                ) : (
                  <div className="property-move-requests-management">
                    {/* Фильтры */}
                    <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: '200px' }}>
                        <input
                          type="text"
                          placeholder="Hledat podle textu..."
                          value={propertyMoveRequestsSearchText}
                          onChange={(e) => setPropertyMoveRequestsSearchText(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            fontSize: '1rem',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                      <div style={{ minWidth: '200px' }}>
                        <select
                          value={propertyMoveRequestsStatusFilter}
                          onChange={(e) => setPropertyMoveRequestsStatusFilter(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            fontSize: '1rem',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            boxSizing: 'border-box'
                          }}
                        >
                          <option value="all">Všechny statusy</option>
                          {propertyMoveRequestsUniqueStatuses.map(status => (
                            <option key={status} value={status}>
                              {getPropertyMoveRequestStatusLabel(status)}
                            </option>
                          ))}
                        </select>
                      </div>
                      {propertyMoveRequestsSearchText && (
                        <button
                          onClick={() => setPropertyMoveRequestsSearchText('')}
                          style={{
                            padding: '0.5rem 1rem',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            backgroundColor: 'white',
                            cursor: 'pointer'
                          }}
                        >
                          ✕
                        </button>
                      )}
                    </div>

                    {filteredPropertyMoveRequests.length === 0 ? (
                      <p style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                        {propertyMoveRequestsSearchText || propertyMoveRequestsStatusFilter !== 'all' ? 'Žádné žádosti neodpovídají filtrům' : 'Žádné žádosti o přesun majetku'}
                      </p>
                    ) : (
                      <>
                        <table className="purchase-requests-admin-table">
                          <thead>
                            <tr>
                              <th>Název předmětu</th>
                              <th>Inventární číslo</th>
                              <th>Odkud</th>
                              <th>Kam</th>
                              <th>Komu předat</th>
                              <th>Vytvořil</th>
                              <th>Status</th>
                              <th>Datum vytvoření</th>
                              <th>Akce</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedPropertyMoveRequests.map((request: any) => (
                              <tr key={request.id}>
                                <td>{request.item_name || '—'}</td>
                                <td>{request.inventory_number}</td>
                                <td>
                                  {request.from_building_name}<br />
                                  <small style={{ color: '#666' }}>{request.from_location}</small>
                                </td>
                                <td>
                                  {request.is_warehouse ? (
                                    <span style={{ fontWeight: 'bold', color: '#2c5aa0' }}>Sklad</span>
                                  ) : (
                                    <>
                                      {request.to_building_name || '-'}<br />
                                      <small style={{ color: '#666' }}>{request.to_location || '-'}</small>
                                    </>
                                  )}
                                </td>
                                <td>{request.assigned_to_name || '-'}</td>
                                <td>{request.requester_name || '-'}</td>
                                <td>
                                  <span className={`status-badge status-${request.status}`}>
                                    {request.status === 'new' ? 'Nová' :
                                      request.status === 'in_progress' ? 'V řešení' :
                                        request.status === 'completed' ? 'Dokončeno' :
                                          request.status === 'rejected' ? 'Zamítnuto' :
                                            request.status === 'awaiting_recipient_response' ? 'Čeká na odpověď příjemce' :
                                              request.status === 'recipient_accepted_self_transport' ? 'Příjemce přijal - vlastní přesun' :
                                                request.status === 'recipient_needs_transport' ? 'Příjemce potřebuje přesun' :
                                                  request.status === 'awaiting_transport' ? 'Čeká na přesun' :
                                                    request.status}
                                  </span>
                                </td>
                                <td>{new Date(request.created_at).toLocaleString('cs-CZ')}</td>
                                <td>
                                  <button
                                    className="btn-delete-small delete-button"
                                    onClick={() => handleDeletePropertyMoveRequest(request.id)}
                                  >
                                    🗑️ Smazat
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {propertyMoveRequestsTotalPages > 1 && (
                          <Pagination
                            currentPage={propertyMoveRequestsCurrentPage}
                            totalPages={propertyMoveRequestsTotalPages}
                            onPageChange={setPropertyMoveRequestsCurrentPage}
                            itemsPerPage={propertyMoveRequestsItemsPerPage}
                            totalItems={filteredPropertyMoveRequests.length}
                          />
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {settingsSubTab === 'system' && (
              adminType === 'spravce_it' ? (
                <SystemSettings onImportCSV={handleImportCSV} />
              ) : (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                  <p>Přístup pouze pro správce IT</p>
                </div>
              )
            )}

            {settingsSubTab === 'it_admin_info' && (
              <div className="settings-section">
                {adminType === 'spravce_it' ? (
                  <>
                    <ITAdminInfoEditor />
                    <ItAssetsAccessPanel />
                  </>
                ) : (
                  <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                    <p>Přístup pouze pro správce IT</p>
                  </div>
                )}
              </div>
            )}

            {settingsSubTab === 'weekly_news' && (
              <div className="settings-section">
                {(adminType === 'spravce_it' || adminType === 'spravce_budov') ? (
                  <WeeklyNewsEditor />
                ) : (
                  <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                    <p>Přístup pouze pro správce IT nebo správce budov</p>
                  </div>
                )}
              </div>
            )}

            {settingsSubTab === 'school_employees' && (
              <div className="settings-section">
                {adminType === 'spravce_it' ? (
                  <SchoolEmployeesEditor />
                ) : (
                  <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                    <p>Přístup pouze pro správce IT</p>
                  </div>
                )}
              </div>
            )}

            {settingsSubTab === 'module_access' && adminType === 'spravce_it' && (
              <ModuleDevAccessPanel />
            )}

            {settingsSubTab === 'backup' && (
              <div className="settings-section">
                {adminType === 'spravce_it' ? (
                  <DatabaseBackup />
                ) : (
                  <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                    <p>Přístup pouze pro správce IT</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {selectedTicket && (
        <AssignExecutorModal
          ticket={selectedTicket}
          onAssign={handleAssignExecutor}
          onClose={() => setSelectedTicket(null)}
        />
      )}

      {showStatistics && adminType === 'spravce_budov' && (
        <ExecutorStatistics onClose={() => setShowStatistics(false)} />
      )}
    </div>
  )
}

// Компонент для управления настройками systému
interface SystemSettingsProps {
  onImportCSV?: (event: React.ChangeEvent<HTMLInputElement>) => void
}

function SystemSettings({ onImportCSV }: SystemSettingsProps) {
  const [calendarUrl, setCalendarUrl] = useState('')
  const [travelSupervisorRoles, setTravelSupervisorRoles] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [savingTravelSupervisors, setSavingTravelSupervisors] = useState(false)

  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    try {
      setLoading(true)
      const url = await getSetting('calendar_url')
      setCalendarUrl(url || '')

      const travelRolesJson = await getSetting('travel_supervisor_roles')
      if (travelRolesJson) {
        try {
          setTravelSupervisorRoles(JSON.parse(travelRolesJson))
        } catch (e) {
          console.error('Error parsing travel supervisor roles:', e)
          setTravelSupervisorRoles(['vedouci', 'director', 'financist', 'admin:spravce_budov', 'zastupce_reditele'])
        }
      } else {
        setTravelSupervisorRoles(['vedouci', 'director', 'financist', 'admin:spravce_budov', 'zastupce_reditele'])
      }
    } catch (error) {
      console.error('Error loading settings:', error)
      alert('Chyba při načítání nastavení')
    } finally {
      setLoading(false)
    }
  }


  const handleSave = async () => {
    try {
      setSaving(true)
      await setSetting(
        'calendar_url',
        calendarUrl,
        'URL nebo odkaz na .ics soubor kalendáře pro intranet'
      )
      alert('Nastavení bylo úspěšně uloženo')
    } catch (error) {
      console.error('Error saving settings:', error)
      alert('Chyba při ukládání nastavení')
    } finally {
      setSaving(false)
    }
  }

  const toggleTravelRole = (roleId: string) => {
    setTravelSupervisorRoles((prev) =>
      prev.includes(roleId) ? prev.filter((r) => r !== roleId) : [...prev, roleId]
    )
  }

  const handleSaveTravelSupervisors = async () => {
    try {
      setSavingTravelSupervisors(true)
      await setSetting(
        'travel_supervisor_roles',
        JSON.stringify(travelSupervisorRoles),
        'Role zobrazené jako schvalovatelé v cestovních příkazech (stejný princip jako dovolená)'
      )
      alert('Nastavení vedoucích pro cestovní příkaz bylo uloženo')
    } catch (error) {
      console.error('Error saving travel supervisor roles:', error)
      alert('Chyba při ukládání')
    } finally {
      setSavingTravelSupervisors(false)
    }
  }

  const AVAILABLE_ROLES = [
    { id: 'vedouci', label: 'Vedoucí' },
    { id: 'director', label: 'Ředitel' },
    { id: 'financist', label: 'Hlavní ekonom' },
    { id: 'referent_eko', label: 'Referent EKO' },
    { id: 'personalista', label: 'Personalista' },
    { id: 'admin:spravce_budov', label: 'Správce budov (Admin)' },
    { id: 'admin:spravce_it', label: 'Správce IT (Admin)' },
    { id: 'vedouci_executor', label: 'Vedoucí řešitel' },
    { id: 'referent', label: 'Referent' },
    { id: 'property_referent', label: 'Majetkový referent' },
    { id: 'spravce_rozvrhu', label: 'Správce rozvrhu' },
    { id: 'spravce_jidelny', label: 'Správce jídelny' },
    { id: 'zocmos', label: 'ZOCMOS' },
    { id: 'zastupce_reditele', label: 'Zástupce ředitele' }
  ]


  if (loading) {
    return <div className="loading">Načítání nastavení...</div>
  }

  return (
    <div className="settings-section" style={{ padding: '2rem', maxWidth: '1000px' }}>
      <h2>Nastavení systému</h2>

      {/* Секция импорта пользователей */}
      <div style={{
        marginBottom: '2rem',
        padding: '1.5rem',
        background: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #dee2e6'
      }}>
        <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>Import uživatelů z CSV</h3>
        <p style={{ color: '#666', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
          Importujte uživatele z CSV souboru exportovaného z Azure AD.
          CSV musí obsahovat kolonky <strong>"userPrincipalName"</strong> (email) a <strong>"displayName"</strong> (jméno).
        </p>
        <p style={{ color: '#666', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
          Existující uživatelé budou aktualizováni (pouze jméno), noví uživatelé budou vytvořeni s rolí "requester".
        </p>

        <label
          style={{
            display: 'inline-block',
            padding: '0.75rem 1.5rem',
            background: loading ? '#ccc' : '#2196f3',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '1rem',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontWeight: '500',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => !loading && (e.currentTarget.style.background = '#1976d2')}
          onMouseOut={(e) => !loading && (e.currentTarget.style.background = '#2196f3')}
        >
          📁 Vybrat CSV soubor
          <input
            type="file"
            accept=".csv"
            onChange={onImportCSV}
            disabled={loading}
            style={{ display: 'none' }}
          />
        </label>

        <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#fff3cd', borderRadius: '6px', border: '1px solid #ffc107' }}>
          <strong style={{ display: 'block', marginBottom: '0.25rem', color: '#856404' }}>
            ⚠️ DŮLEŽITÉ:
          </strong>
          <p style={{ margin: 0, fontSize: '0.85rem', color: '#856404' }}>
            Role a admin_type se <strong>NEMĚNÍ</strong> - ty nastavují pouze administrátoři ručně.
          </p>
        </div>
      </div>

      {/* Секция настроек календаря */}
      <div style={{
        marginBottom: '2rem',
        padding: '1.5rem',
        background: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #dee2e6'
      }}>
        <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>Kalendář pro intranet</h3>
        <p style={{ color: '#666', marginBottom: '1rem', fontSize: '0.95rem' }}>
          Zadejte URL nebo odkaz na .ics soubor kalendáře, který bude použit v aplikaci pro zobrazení kalendáře.
          Můžete zadat buď přímý odkaz na .ics soubor nebo webcal:// odkaz.
        </p>
        <input
          type="text"
          id="calendar-url"
          value={calendarUrl}
          onChange={(e) => setCalendarUrl(e.target.value)}
          placeholder="https://outlook.office365.com/owa/calendar/.../calendar.ics"
          style={{
            width: '100%',
            padding: '0.75rem',
            fontSize: '1rem',
            border: '1px solid #ddd',
            borderRadius: '6px',
            marginTop: '0.5rem',
            boxSizing: 'border-box'
          }}
        />
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            marginTop: '1rem',
            padding: '0.75rem 1.5rem',
            fontSize: '1rem',
            backgroundColor: saving ? '#ccc' : '#14b8a6',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontWeight: '500',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => !saving && (e.currentTarget.style.background = '#0d9488')}
          onMouseOut={(e) => !saving && (e.currentTarget.style.background = '#14b8a6')}
        >
          {saving ? 'Ukládání...' : '💾 Uložit nastavení kalendáře'}
        </button>
      </div>

      <div style={{
        marginBottom: '2rem',
        padding: '1.5rem',
        background: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #dee2e6'
      }}>
        <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>Vedoucí pro cestovní příkaz (CP)</h3>
        <p style={{ color: '#666', marginBottom: '1rem', fontSize: '0.95rem' }}>
          Stejný princip jako u dovolené: vyberte role uživatelů, kteří se objeví v seznamu schvalovatelů při zakládání cestovního příkazu.
        </p>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: '0.75rem',
          margin: '1.5rem 0'
        }}>
          {AVAILABLE_ROLES.map(role => (
            <label key={`travel-${role.id}`} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem',
              background: 'white',
              borderRadius: '6px',
              border: '1px solid #eee',
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={travelSupervisorRoles.includes(role.id)}
                onChange={() => toggleTravelRole(role.id)}
                style={{ width: '18px', height: '18px' }}
              />
              <span style={{ fontSize: '0.9rem' }}>{role.label}</span>
            </label>
          ))}
        </div>
        <button
          onClick={handleSaveTravelSupervisors}
          disabled={savingTravelSupervisors}
          style={{
            padding: '0.75rem 1.5rem',
            fontSize: '1rem',
            backgroundColor: savingTravelSupervisors ? '#ccc' : '#0d9488',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: savingTravelSupervisors ? 'not-allowed' : 'pointer',
            fontWeight: '500',
          }}
        >
          {savingTravelSupervisors ? 'Ukládání...' : '💾 Uložit vedoucí pro CP'}
        </button>
      </div>

      {/* Секция настроек логотипа */}
      <div style={{
        marginBottom: '2rem',
        padding: '1.5rem',
        background: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #dee2e6'
      }}>
        <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>Logo školy</h3>
        <p style={{ color: '#666', marginBottom: '1rem', fontSize: '0.95rem' }}>
          Logo školy je nastaveno na výchozí hodnotu a zobrazuje se automaticky v celém intranetu.
        </p>
        <div style={{
          marginTop: '1rem',
          padding: '1rem',
          backgroundColor: '#ffffff',
          borderRadius: '6px',
          border: '1px solid #dee2e6'
        }}>
          <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.75rem', fontWeight: '500' }}>Aktuální logo:</p>
          <img
            src="/logo.png"
            alt="School Logo"
            style={{
              maxWidth: '200px',
              maxHeight: '100px',
              objectFit: 'contain',
              display: 'block'
            }}
            onError={(e) => {
              e.currentTarget.src = '/logo.png'
            }}
          />
        </div>
      </div>
    </div>
  )
}

export default AdminView


